import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { useGoBack } from "../../../hooks/useGoBack";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import {
  archiveQuizApi,
  CAMPAIGN_STATUS_LABEL,
  duplicateQuizApi,
  fmtDateTime,
  fmtScore,
  listQuizCampaignsApi,
  listQuizzesApi,
  type QuizCampaign,
  type QuizListItem,
} from "../quizApi";
import { QUIZ_CAMPAIGN_NEW_PAGE, QUIZ_CAMPAIGN_PAGE, QUIZ_EDIT_PAGE, QUIZ_NEW_PAGE } from "../routes";
import "./Quiz.css";

type Tab = "campaigns" | "quizzes";

const TABS: { value: Tab; label: string }[] = [
  { value: "campaigns", label: "Campanhas" },
  { value: "quizzes", label: "Quizzes" },
];

export const STATUS_PILL: Record<QuizCampaign["status"], string> = {
  ABERTA: "ecoPill",
  AGENDADA: "ecoPill quizPill--warn",
  ENCERRADA: "ecoPill ecoPill--off",
};

const QuizHubPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(OPERACOES_PAGE());
  const [tab, setTab] = useState<Tab>("campaigns");
  const [campaigns, setCampaigns] = useState<QuizCampaign[] | null>(null);
  const [quizzes, setQuizzes] = useState<QuizListItem[] | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<QuizListItem | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, q] = await Promise.all([listQuizCampaignsApi(), listQuizzesApi()]);
      setCampaigns(c);
      setQuizzes(q);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao carregar.");
      setCampaigns([]);
      setQuizzes([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDuplicate = async (quiz: QuizListItem) => {
    try {
      setBusy(true);
      const copy = await duplicateQuizApi(quiz.id);
      toast.success("Quiz duplicado.");
      navigate(QUIZ_EDIT_PAGE(copy.id));
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      setBusy(true);
      await archiveQuizApi(archiveTarget.id);
      toast.success("Quiz arquivado.");
      setArchiveTarget(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setBusy(false);
    }
  };

  const adhesion = (c: QuizCampaign) => {
    const rate = c.participantCount ? c.submittedCount / c.participantCount : 0;
    return (
      <span className="quizBar">
        <span className="quizBar__track" aria-hidden="true">
          <span className="quizBar__fill" style={{ width: `${Math.round(rate * 100)}%` }} />
        </span>
        <span className="quizBar__label">
          {c.submittedCount} de {c.participantCount}
        </span>
      </span>
    );
  };

  const renderCampaigns = () => {
    if (!campaigns) return <p className="quizPage__loading">Carregando...</p>;
    if (!campaigns.length) {
      return (
        <div className="quizPanel__pad">
          <p className="ecoEmpty">Nenhuma campanha ainda. Monte um quiz e publique para os supervisores.</p>
        </div>
      );
    }
    return (
      <>
        <div className="ecoTableWrap quizDesktop">
          <table className="ecoTable">
            <thead>
              <tr>
                <th scope="col">Campanha</th>
                <th scope="col">Quiz</th>
                <th scope="col">Prazo</th>
                <th scope="col">Status</th>
                <th scope="col">Responderam</th>
                <th scope="col" className="ecoTable__num">Nota média</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="quizTable__row" onClick={() => navigate(QUIZ_CAMPAIGN_PAGE(c.id))}>
                  <td>
                    <button type="button" className="ecoLinkBtn quizTable__strong" onClick={() => navigate(QUIZ_CAMPAIGN_PAGE(c.id))}>
                      {c.title}
                    </button>
                  </td>
                  <td className="ecoTable__muted">{c.quiz.title}</td>
                  <td className="ecoTable__muted">{fmtDateTime(c.closesAt)}</td>
                  <td>
                    <span className={STATUS_PILL[c.status]}>{CAMPAIGN_STATUS_LABEL[c.status]}</span>
                  </td>
                  <td>{adhesion(c)}</td>
                  <td className="ecoTable__num">{fmtScore(c.averageScore)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="quizCards quizMobile">
          {campaigns.map((c) => (
            <li key={c.id}>
              <button type="button" className="quizCard" onClick={() => navigate(QUIZ_CAMPAIGN_PAGE(c.id))}>
                <span className="quizCard__top">
                  <span className="quizCard__text">
                    <span className="quizCard__title">{c.title}</span>
                    <span className="quizCard__meta">
                      {c.quiz.title} · até {fmtDateTime(c.closesAt)}
                    </span>
                  </span>
                  <span className={STATUS_PILL[c.status]}>{CAMPAIGN_STATUS_LABEL[c.status]}</span>
                </span>
                <span className="quizCard__row">
                  {adhesion(c)}
                  <span className="quizCard__meta">Média {fmtScore(c.averageScore)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </>
    );
  };

  const quizActions = (q: QuizListItem) => (
    <div className="quizActions">
      <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => navigate(QUIZ_EDIT_PAGE(q.id))}>
        {q.locked ? "Ver" : "Editar"}
      </button>
      <button
        type="button"
        className="ecoBtn ecoBtn--sm ecoBtn--primary"
        onClick={() => navigate(`${QUIZ_CAMPAIGN_NEW_PAGE()}?quizId=${q.id}`)}
      >
        Publicar
      </button>
      <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => handleDuplicate(q)} disabled={busy}>
        Duplicar
      </button>
      <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--danger" onClick={() => setArchiveTarget(q)} disabled={busy}>
        Arquivar
      </button>
    </div>
  );

  const renderQuizzes = () => {
    if (!quizzes) return <p className="quizPage__loading">Carregando...</p>;
    if (!quizzes.length) {
      return (
        <div className="quizPanel__pad">
          <p className="ecoEmpty">Nenhum quiz ainda. Clique em "Novo quiz" para montar o primeiro.</p>
        </div>
      );
    }
    return (
      <>
        <div className="ecoTableWrap quizDesktop">
          <table className="ecoTable">
            <thead>
              <tr>
                <th scope="col">Quiz</th>
                <th scope="col" className="ecoTable__num">Perguntas</th>
                <th scope="col" className="ecoTable__num">Campanhas</th>
                <th scope="col">Situação</th>
                <th scope="col" className="ecoTable__num">Ações</th>
              </tr>
            </thead>
            <tbody>
              {quizzes.map((q) => (
                <tr key={q.id}>
                  <td className="quizTable__strong">{q.title}</td>
                  <td className="ecoTable__num">{q.questionCount}</td>
                  <td className="ecoTable__num">{q.campaignCount}</td>
                  <td>
                    {q.locked ? <span className="ecoPill ecoPill--off">Já respondido</span> : <span className="ecoPill">Editável</span>}
                  </td>
                  <td className="ecoTable__num">{quizActions(q)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="quizCards quizMobile">
          {quizzes.map((q) => (
            <li key={q.id} className="quizCard quizCard--static">
              <span className="quizCard__top">
                <span className="quizCard__text">
                  <span className="quizCard__title">{q.title}</span>
                  <span className="quizCard__meta">
                    {q.questionCount} perguntas · {q.campaignCount} campanhas
                  </span>
                </span>
                {q.locked ? <span className="ecoPill ecoPill--off">Já respondido</span> : null}
              </span>
              {quizActions(q)}
            </li>
          ))}
        </ul>
      </>
    );
  };

  return (
    <div className="quizPage">
      <main className="quizPage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Quiz" },
          ]}
          title="Quiz para supervisores"
          subtitle="Monte quizzes com gabarito e publique com prazo. O supervisor responde pelo link, entrando com o CPF."
          onBack={goBack}
          backLabel="Voltar para Operações"
          actions={
            <>
              <button type="button" className="ecoBtn" onClick={() => navigate(QUIZ_NEW_PAGE())}>
                <EcoIcon name="plus" size={18} />
                Novo quiz
              </button>
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => navigate(QUIZ_CAMPAIGN_NEW_PAGE())}>
                <EcoIcon name="link" size={18} />
                Publicar quiz
              </button>
            </>
          }
        />

        <section className="ecoCard quizPanel quizPanel--flush">
          <div className="quizPanel__head">
            <EcoSegmented label="Mostrar" options={TABS} value={tab} onChange={setTab} />
          </div>
          {tab === "campaigns" ? renderCampaigns() : renderQuizzes()}
        </section>
      </main>

      <EcoConfirmDialog
        open={Boolean(archiveTarget)}
        title="Arquivar quiz?"
        message={
          archiveTarget ? (
            <>
              O quiz <strong>{archiveTarget.title}</strong> sai da lista e não pode mais ser publicado. As campanhas e os
              resultados dele continuam disponíveis.
            </>
          ) : null
        }
        confirmLabel="Arquivar"
        tone="danger"
        busy={busy}
        onConfirm={handleArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
};

export default QuizHubPage;
